<template>
  <div class="explorer">
    <div class="explorer-buttons-bar d-flex justify-content-between">
      <div>
        <input id="newFile" ref="newFile" type="file" name="file" class="d-none" @change="uploadFile($event.target.name, $event.target.files)">
        <button v-if="$store.getters.bscFeature($store.getters.gateway, 'fileExchange')" id="newFileButton" :title="$t('g-fileExplorer:newFile') | capitalize" class="btn" @click.prevent="$refs.newFile.click()"><i class="icon-fe-new-file" /></button>
        <button id="newFolderButton" :title="$t('g-fileExplorer:newFolder') | capitalize" class="btn" @click.prevent="addNewFolder"><i class="icon-fe-new-folder" /></button>
        <button id="editButton" :title="$t('g-fileExplorer:rename') | capitalize" :disabled="isEmpty(selectedElement) || selectedElement.name==='/'" class="btn" @click.prevent="activateEdit"><i class="icon-edit" /></button>
        <button id="deleteButton" :title="$t('g-fileExplorer:delete') | capitalize" :disabled="isEmpty(selectedElement) || selectedElement.name==='/'" class="btn" @click.prevent="confirmRemove"><i class="icon-fe-delete" /></button>
        <button id="copyButton" :title="$t('g-fileExplorer:copy') | capitalize" :disabled="isEmpty(selectedElement) || selectedElement.name==='/'" class="btn" @click.prevent="copy"><i class="icon-fe-copy" /></button>
        <button id="cutButton" :title="$t('g-fileExplorer:cut') | capitalize" :disabled="isEmpty(selectedElement) || selectedElement.name==='/'" class="btn" @click.prevent="cut"><i class="icon-fe-cut" /></button>
        <button id="pasteButton" :title="$t('g-fileExplorer:paste') | capitalize" :disabled="isEmpty(clipboard)" class="btn" @click.prevent="paste"><i class="icon-fe-paste" /></button>
      </div>

      <div>
        <button id="refreshButton" :title="$t('g-fileExplorer:refresh') | capitalize" class="btn" @click.prevent="refresh"><i class="icon-reset" /></button>
        <button id="gridButton" :title="$t('g-fileExplorer:displayIcons') | capitalize" class="btn" @click.prevent="setMode('icons')"><i class="icon-fe-grid" /></button>
        <button id="listButton" :title="$t('g-fileExplorer:displayList') | capitalize" class="btn" @click.prevent="setMode('list')"><i class="icon-fe-list" /></button>
        <button id="detailsButton" :title="$t('g-fileExplorer:displayDetails') | capitalize" class="btn" @click.prevent="setMode('details')"><i class="icon-fe-details" /></button>
      </div>
    </div>

    <div class="explorer-files" @contextmenu.prevent="rightClick">
      <ul :class="mode">
        <explorer-node v-for="(content, key) in directoryContent" :key="key" :class="mode"
                       :item="content"
                       :mode="mode"
                       :click-handler="click"
                       :dblclick-handler="dblclick"
        />
      </ul>

      <context-menu ref="contextMenu">
        <li @click="refresh">{{ $t('g-fileExplorer:refresh') | capitalize }}</li>
        <li @click="addNewFolder">{{ $t('g-fileExplorer:newFolder') | capitalize }}</li>
        <li v-if="!isEmpty(clipboard)" @click="paste">{{ $t('g-fileExplorer:paste') | capitalize }}</li>
      </context-menu>
    </div>

    <confirm id="removeConfirm"
             v-model="showRemoveConfirm"
             :message="confirmMessage"
             @confirm:validate="remove"
    />
  </div>
</template>

<script>
import { isEmpty } from 'lodash-es'
import { mapGetters } from 'vuex'
import eventBus from '@/modules/eventBus'
import storageKeysConstants from '@/configuration/storageKeys.constants'

import ExplorerNode from './ExplorerNode'

export default {
  components: { ExplorerNode },

  data () {
    return {
      // icons/list/details
      mode: 'icons',
      // Array of new folders (will always be a one element array)
      newFolders: [],
      // Show / hide confirm box for deletion
      showRemoveConfirm: false,
      confirmMessage: '',
      itemToRemove: null
    }
  },

  computed: {
    ...mapGetters(['treeNodes', 'selectedElement', 'clipboard', 'currentDirectory', 'currentDirectoryContent']),

    directoryContent () {
      return [...this.currentDirectoryContent, ...this.newFolders]
    }
  },

  mounted () {
    // Listen to external event
    eventBus.$on('fileExplorer:refresh', this.refresh)
    eventBus.$on('fileExplorer:navigate', this.cancelNewFolder)
    eventBus.$on('fileExplorer:copy', this.copy)
    eventBus.$on('fileExplorer:download', this.download)
    eventBus.$on('fileExplorer:cut', this.cut)
    eventBus.$on('fileExplorer:paste', this.paste)
    eventBus.$on('fileExplorer:delete', this.confirmRemove)

    // Get saved mode
    const savedMode = window.localStorage.getItem(storageKeysConstants.FILE_MODE)
    if (savedMode) {
      this.setMode(savedMode)
    }
  },

  destroyed () {
    eventBus.$off('fileExplorer:refresh', this.refresh)
    eventBus.$off('fileExplorer:navigate', this.cancelNewFolder)
    eventBus.$off('fileExplorer:copy', this.copy)
    eventBus.$off('fileExplorer:download', this.download)
    eventBus.$off('fileExplorer:cut', this.cut)
    eventBus.$off('fileExplorer:paste', this.paste)
    eventBus.$off('fileExplorer:delete', this.confirmRemove)
  },

  methods: {
    isEmpty (v) { return isEmpty(v) },

    // Upload file, not multiple upload, only one file accepted
    uploadFile (fieldName, files) {
      this.$store.dispatch('uploadFile', {
        fieldName,
        file: files[0]
      })
        .then(() => {
        // Clear input
          this.$refs.newFile.value = ''
          // Refresh
          this.refresh()
        })
        .catch(() => {
        // Clear input
          this.$refs.newFile.value = ''
        })
    },

    // Create new folder
    addNewFolder () {
      if (this.newFolders.length === 0) {
        this.newFolders.push({
          directory: true,
          name: 'new_folder',
          isNew: true,
          children: []
        })
      }
    },

    // Reset new folders array
    cancelNewFolder () {
      this.newFolders = []
    },

    // Edit selected element
    activateEdit () {
      eventBus.$emit('fileExplorer:editSelection')
    },

    // Download selected element
    download () {
      this.$store.dispatch('getFile')
    },

    // Copy selected element or chosen element (in options, from context menu)
    copy (options = {}) {
      options.mode = 'copy'
      this.$store.commit('setClipboard', options)
    },

    // Cut selected element or chosen element (in options, from context menu)
    cut (options = {}) {
      options.mode = 'cut'
      this.$store.commit('setClipboard', options)
    },

    // Paste clipboard in current directory or on chosen directory (in options, from context menu)
    paste (options = {}) {
      this.$store.dispatch('paste', options).then(this.refresh)
    },

    // Confirm deletion
    confirmRemove (options = {}) {
      this.itemToRemove = !isEmpty(options.element) ? options.element : this.selectedElement
      this.confirmMessage = this.$t('g-fileExplorer:confirmDelete', { item: this.itemToRemove ? this.itemToRemove.name : '' })
      this.showRemoveConfirm = true
    },

    // Delete
    remove () {
      this.$store.dispatch('remove', { element: this.itemToRemove }).then(this.refresh)
    },

    // Refresh current directory
    refresh () {
      // Find tree node
      let treeNode

      if (isEmpty(this.currentDirectory) || this.currentDirectory === '/') {
        // Root node
        treeNode = this.treeNodes
      } else {
        // Find node in tree
        treeNode = this.$utils.findInTree(this.treeNodes.children, this.currentDirectory)
      }
      this.$store.dispatch('getDirectory', {
        directory: isEmpty(this.currentDirectory) ? '/' : this.currentDirectory,
        parentNode: treeNode,
        changeCurrentDirectory: true,
        forceRefresh: true
      })
        .then(() => { this.newFolders = [] })
    },

    // Set display mode: list/icons
    setMode (mode) {
      this.mode = mode
      // Save in local storage
      window.localStorage.setItem(storageKeysConstants.FILE_MODE, mode)
    },

    // Context menu event on right click
    rightClick (event) {
      // Clear all selected
      eventBus.$emit('fileExplorer:unselect')

      // Open context menu
      this.$refs.contextMenu.open(event)
    },

    // Click on an item
    click (element) {
      // Send naviagtion event
      eventBus.$emit('fileExplorer:navigate')

      // Set selected item
      this.$store.commit('setSelectedElement', element)
    },

    // Double click on an item, will be triggered after click
    dblclick (element) {
      // Send naviagtion event
      eventBus.$emit('fileExplorer:navigate')

      // Do just a click on files
      if (!element.directory) return

      // Open directory
      this.$store.dispatch('getDirectory', {
        directory: element.path,
        parentNode: element,
        changeCurrentDirectory: true
      })
        .then(() => {
          eventBus.$emit('fileExplorer:unselect')

          // Reset selected item as we are openning a folder
          this.$store.commit('setSelectedElement', null)
        })
    }
  }
}
</script>

<style scoped lang="scss">
  @import "~@/scss/variables";

  .explorer {
    height: 600px;
    margin-bottom: 15px;
    color: $primary;

    .explorer-buttons-bar {
      margin-bottom: 15px;

      .btn {
        width: 40px;
        height: 40px;
        padding: 2px;
        margin-right: 0.25rem;
        margin-bottom: 0.25rem;
        font-size: 14px;
        color: $primary;
        background-color: $gray-300;
        box-shadow: none;

        i.icon-folder {
          display: inline-block;
          width: 21px;
          height: 21px;
          padding: 0;
          margin: 0;
          line-height: 0;
          cursor: pointer;
          background-color: transparent;
          background-image: url("~@/img/file-explorer.png");
          background-repeat: no-repeat;
          background-attachment: scroll;
          background-position: -147px 0;
          border: 0 none;
          outline: none;
        }

        &:hover {
          background-color: $gray-400;
        }

        &[disabled] {
          cursor: not-allowed;

          i {
            cursor: not-allowed;
          }
        }
      }
    }

    .explorer-files {
      height: 560px;
      overflow-y: auto;
      font-size: 12px;

      ul {
        height: 100%;
        padding: 0;
        margin: 0;
        list-style: none;

        &.list {
          column-width: 200px;
          column-fill: auto;
          column-gap: 10px;
        }

        &.details {
          column-width: 100%;
          column-fill: auto;
          column-gap: 10px;
        }
      }
    }
  }
</style>
